// Área OFERTAS & CRIATIVOS.
// Abas por oferta + visão geral (cards ROAS/KPIs/verdict), detalhe com campos
// meta editáveis, KPIs, tabela de criativos (cpaReal + statusRow por linha),
// add/del de linha e "Nova oferta".
//
// A lógica de cálculo (cpaReal, statusRow, ostats, verdict) é cópia EXATA do
// app legado, apenas adaptada de arrays posicionais para objetos nomeados.
import { num, brl, esc, svgDel, maskBRDate, dateFieldState } from '../lib/format.js'
import { marcarSujo, marcarLimpo } from '../lib/save-status.js'
import { kpiHTML, debounce } from './shared.js'
import { state, posicaoFim } from '../store.js'
import {
  ofertasRepo,
  criativosRepo,
  ofertaValorColuna,
  OFERTA_COL,
  criativoValorColuna,
  CRIATIVO_COL
} from '../data/repositories.js'

// Estado de navegação local da área (igual ao legado: active/overview).
let active = 0
let overview = true
// Filtro das ofertas (abas + visão geral): todas | ativas | inativas.
let ofFilter = 'todas'

// Status manual da oferta (campo `ativa` no banco; default ativa).
function ofertaMatch(of) {
  if (ofFilter === 'ativas') return of.ativa !== false
  if (ofFilter === 'inativas') return of.ativa === false
  return true
}
// Monta os chips do filtro de ofertas (no cabeçalho da área) e liga os cliques.
function renderOfFilter() {
  const el = document.getElementById('ofFilter')
  if (!el) return
  const opts = [
    ['todas', 'Todas'],
    ['ativas', 'Ativas'],
    ['inativas', 'Inativas']
  ]
  el.innerHTML = opts
    .map(([f, lbl]) => '<span class="chip' + (ofFilter === f ? ' on' : '') + '" data-f="' + f + '">' + lbl + '</span>')
    .join('')
  el.querySelectorAll('.chip').forEach(ch => {
    ch.onclick = () => {
      ofFilter = ch.dataset.f
      overview = true // volta pra lista filtrada, sem ficar preso na oferta atual
      render()
    }
  })
}

// Toast injetado pelo main (evita dependência circular com shared/main).
let toast = () => {}
export function setToast(fn) {
  toast = fn
}

// Criativo em branco (status ATIVA por padrão), espelhando emptyRow() legado.
function criativoVazio(ofertaId, posicao) {
  return {
    id: crypto.randomUUID(),
    oferta_id: ofertaId,
    data: '',
    conta: '',
    status: 'ATIVA',
    criativo: '',
    gestor: '',
    gasto: null,
    ic: null,
    vendas: null,
    posicao
  }
}

// Oferta em branco com 3 criativos, espelhando blankOffer() legado.
function ofertaVazia() {
  return {
    id: crypto.randomUUID(),
    codigo: '',
    gestor: '',
    data: '',
    ticket: 297,
    ic: 30,
    cpa: 150,
    ativa: true,
    posicao: 0,
    criativos: []
  }
}

/* ============ Cálculos (cópia EXATA do legado) ============ */

// CPA real por linha. g=gasto, ic=ic, v=vendas.
function cpaReal(c) {
  const g = num(c.gasto),
    ic = num(c.ic),
    v = num(c.vendas)
  if (g == null) return ''
  if (v != null && v > 0) return 'R$ ' + brl(g / v)
  if (ic != null && ic > 0) return 'Só IC'
  return 'Sem venda'
}

// Badge de status por linha. Reproduz a lógica posicional original:
//   legado usava conta=r[2] (que é a coluna STATUS "ATIVA/INATIVA").
function statusRow(c, of) {
  const g = num(c.gasto),
    ic = num(c.ic),
    v = num(c.vendas),
    statusConta = c.status
  const ICm = num(of.ic),
    CPAm = num(of.cpa),
    TK = num(of.ticket)
  if (g == null) return ''
  if (CPAm != null && g >= CPAm && (v == null || v === 0)) return 'PAUSAR'
  if (statusConta === 'INATIVA') {
    if ((ic || 0) > 0 || (v || 0) > 0) return 'RETESTAR'
    return ICm != null && g < ICm ? 'RETESTAR' : 'PAUSAR'
  }
  if (ICm != null && g < ICm) return 'TESTE'
  if ((v != null && v > 0 && TK != null && g / v < TK) || (ic != null && ic > 0)) return 'MANTER'
  if ((ic == null || ic === 0) && (v == null || v === 0)) return 'PAUSAR'
  return 'TESTE'
}
const stLabel = { MANTER: 'MANTER', TESTE: 'EM TESTE', PAUSAR: 'PAUSAR', RETESTAR: 'RETESTAR' }

// Estatísticas agregadas da oferta. crit=criativos preenchidos; contas=distintas;
// gasto/ics/vendas=somas; test=linhas com gasto>=of.ic; fat=vendas*ticket; roas=fat/gasto.
function ostats(of) {
  let crit = 0,
    gasto = 0,
    ics = 0,
    vendas = 0,
    test = 0
  const ct = new Set()
  of.criativos.forEach(c => {
    if (String(c.criativo).trim()) crit++
    if (String(c.conta).trim()) ct.add(c.conta.trim())
    const g = num(c.gasto)
    if (g != null) gasto += g
    const ic = num(c.ic)
    if (ic != null) ics += ic
    const v = num(c.vendas)
    if (v != null) vendas += v
    if (g != null && num(of.ic) != null && g >= num(of.ic)) test++
  })
  const fat = vendas * (num(of.ticket) || 0)
  return { crit, contas: ct.size, gasto, ics, vendas, fat, test, roas: gasto > 0 ? fat / gasto : 0 }
}

// Veredito da oferta (validação + decisão por ROAS).
function verdict(s) {
  if (s.test < 20) return ['warn', '⚠', 'Não validada — ' + s.test + '/20 testados']
  if (s.roas >= 1) return ['ok', '🟢', 'ESCALAR · ROAS ' + s.roas.toFixed(2)]
  if (s.roas >= 0.5) return ['warn', '🟡', 'OTIMIZAR · ROAS ' + s.roas.toFixed(2)]
  return ['bad', '🔴', 'MATAR · ROAS ' + s.roas.toFixed(2)]
}
const roasColor = r => (r >= 1 ? 'var(--green)' : r >= 0.5 ? 'var(--amber)' : 'var(--red)')
const roasCls = r => (r >= 1 ? 'g' : r >= 0.5 ? 'a' : 'r')

// Texto completo do veredito (caixa do detalhe).
function verdFull(of, s, vt) {
  return {
    ok: '🟢 ESCALAR — ROAS ' + s.roas.toFixed(2) + ' · ' + s.test + ' criativos testados',
    warn:
      s.test < 20
        ? '⚠️ Oferta ainda não validada — ' +
          s.test +
          '/20 criativos gastaram ≥ R$ ' +
          brl(num(of.ic)) +
          ' (faltam ' +
          (20 - s.test) +
          ')'
        : '🟡 OTIMIZAR — ROAS ' + s.roas.toFixed(2) + ' · ' + s.test + ' criativos testados',
    bad: '🔴 MATAR — ROAS ' + s.roas.toFixed(2) + ' · ' + s.test + ' criativos testados'
  }[vt]
}

// Lista de KPIs do detalhe (reutilizada no render e no refresh).
function kpisDetalheHTML(s) {
  return (
    '<div class="kpis">' +
    kpiHTML('ROAS atual', s.roas.toFixed(2), s.test + '/20 testados', roasCls(s.roas), 'color:' + roasColor(s.roas)) +
    kpiHTML('Criativos', s.crit, s.contas + ' contas', 'b') +
    kpiHTML('Investido', 'R$ ' + brl(s.gasto), '', 'a') +
    kpiHTML('ICs', s.ics, '', 'p') +
    kpiHTML('Vendas', s.vendas, '', '') +
    kpiHTML('Faturamento', 'R$ ' + brl(s.fat), '', 'g') +
    '</div>'
  )
}

/* ============ Render ============ */

// Abas (visão geral + uma por oferta com badge de ROAS).
function renderTabs() {
  const t = document.getElementById('tabs')
  t.innerHTML = ''
  const ov = document.createElement('div')
  ov.className = 'tab ovt' + (overview ? ' active' : '')
  ov.innerHTML =
    '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg> Visão geral'
  ov.onclick = () => {
    overview = true
    render()
  }
  t.appendChild(ov)
  state.ofertas.forEach((of, i) => {
    if (!ofertaMatch(of)) return // filtro de ofertas ativas/inativas
    const s = ostats(of)
    const tab = document.createElement('div')
    tab.className = 'tab' + (!overview && i === active ? ' active' : '') + (of.ativa === false ? ' inativa' : '')
    tab.innerHTML =
      ((of.codigo && of.codigo.trim()) || 'Oferta #' + (i + 1)) +
      ' <span class="badge">' +
      s.roas.toFixed(2) +
      '</span>'
    tab.onclick = () => {
      overview = false
      active = i
      render()
    }
    t.appendChild(tab)
  })
}

export function render() {
  renderOfFilter()
  renderTabs()
  const ov = document.getElementById('overview')
  const dt = document.getElementById('detail')
  ov.classList.toggle('hidden', !overview)
  dt.classList.toggle('hidden', overview)
  if (overview) renderOverview()
  else renderDetail()
}

function renderOverview() {
  const el = document.getElementById('overview')
  const lista = state.ofertas.filter(ofertaMatch)
  let tg = 0,
    tf = 0,
    tv = 0,
    tc = 0
  lista.forEach(of => {
    const s = ostats(of)
    tg += s.gasto
    tf += s.fat
    tv += s.vendas
    tc += s.crit
  })
  const tr = tg > 0 ? tf / tg : 0
  let h =
    '<div class="kpis">' +
    kpiHTML('Ofertas', lista.length, '', '') +
    kpiHTML('Criativos', tc, '', 'b') +
    kpiHTML('Investimento', 'R$ ' + brl(tg), 'total', 'a') +
    kpiHTML('Faturamento', 'R$ ' + brl(tf), tv + ' vendas', 'g') +
    kpiHTML('ROAS geral', tr.toFixed(2), 'fat / gasto', roasCls(tr), 'color:' + roasColor(tr)) +
    '</div>'
  if (!lista.length) {
    const msg =
      ofFilter === 'todas'
        ? 'Nenhuma oferta. Clique em “Nova oferta”.'
        : 'Nenhuma oferta ' + (ofFilter === 'ativas' ? 'ativa' : 'inativa') + '.'
    h += '<div class="empty">' + msg + '</div>'
    el.innerHTML = h
    return
  }
  const maxR = Math.max(1, ...lista.map(of => ostats(of).roas))
  h += '<div class="ovgrid">'
  state.ofertas.forEach((of, i) => {
    if (!ofertaMatch(of)) return
    const s = ostats(of)
    const [vt, ic, vl] = verdict(s)
    const rc = roasColor(s.roas)
    const cm = {
      ok: ['rgba(54,224,164,.1)', 'var(--green)', 'rgba(54,224,164,.3)'],
      warn: ['rgba(245,177,61,.1)', 'var(--amber)', 'rgba(245,177,61,.3)'],
      bad: ['rgba(255,93,93,.1)', 'var(--red)', 'rgba(255,93,93,.3)']
    }[vt]
    h +=
      '<div class="ovc' + (of.ativa === false ? ' inativa' : '') + '" data-i="' +
      i +
      '"><div class="top-c"><div><h3>' +
      ((of.codigo && of.codigo.trim()) || 'Oferta #' + (i + 1)) +
      '</h3><div class="gest">' +
      esc(of.gestor || 'sem gestor') +
      '</div></div><div><div class="roas-big" style="color:' +
      rc +
      '">' +
      s.roas.toFixed(2) +
      '</div><div class="roas-lab">ROAS</div></div></div><div class="track"><div class="fill" style="width:' +
      Math.min(100, (s.roas / maxR) * 100).toFixed(0) +
      '%;background:' +
      rc +
      '"></div></div><div class="ovrow"><span>Investido</span><b>R$ ' +
      brl(s.gasto) +
      '</b></div><div class="ovrow"><span>Faturamento</span><b>R$ ' +
      brl(s.fat) +
      '</b></div><div class="ovrow"><span>Criativos · testados</span><b>' +
      s.crit +
      ' · ' +
      s.test +
      '/20</b></div><div class="ovtag" style="background:' +
      cm[0] +
      ';color:' +
      cm[1] +
      ';border-color:' +
      cm[2] +
      '">' +
      ic +
      ' ' +
      vl +
      '</div></div>'
  })
  h += '</div>'
  el.innerHTML = h
  el.querySelectorAll('.ovc').forEach(c => {
    c.onclick = () => {
      overview = false
      active = +c.dataset.i
      render()
    }
  })
}

// Campo meta editável. `field` é o nome do campo no objeto oferta.
function fldHTML(field, lab, ph, n) {
  const v = state.ofertas[active][field]
  return (
    '<div class="fld"><label>' +
    lab +
    '</label><input data-k="' +
    field +
    '"' +
    (n ? ' data-n="1"' : '') +
    (field === 'data' ? ' inputmode="numeric" maxlength="10"' : '') +
    ' value="' +
    esc(v ?? '') +
    '" placeholder="' +
    ph +
    '"></div>'
  )
}

function renderDetail() {
  const of = state.ofertas[active]
  const el = document.getElementById('detail')
  const s = ostats(of)
  const [vt] = verdict(s)
  const ativa = of.ativa !== false
  let h =
    '<div class="detail-bar">' +
    '<button class="oferta-status ' + (ativa ? 'ativa' : 'inativa') + '" id="ofStatusBtn">' +
    '<span class="dot"></span>' + (ativa ? 'Oferta ativa' : 'Oferta inativa') +
    '</button>' +
    '<button class="btn btn-danger" id="delOfferBtn">' +
    svgDel +
    'Excluir oferta</button>' +
    '</div>' +
    '<div class="meta">' +
    fldHTML('codigo', 'ID da oferta', 'OF-00') +
    fldHTML('gestor', 'Gestor', 'quem cuida') +
    fldHTML('data', 'Data subida', 'dd/mm/aaaa') +
    fldHTML('ticket', 'Ticket (R$)', '0', 1) +
    fldHTML('ic', 'Custo IC máx (R$)', '0', 1) +
    fldHTML('cpa', 'CPA máx (R$)', '0', 1) +
    '</div>'
  h += kpisDetalheHTML(s)
  h += '<div class="verd ' + vt + '" id="vbox">' + verdFull(of, s, vt) + '</div>'
  h +=
    '<div class="tcard"><div class="thead-bar"><h3>Criativos</h3><span class="count" id="ocount">' +
    of.criativos.length +
    ' linhas</span></div><div class="tscroll"><table><thead><tr><th>Data</th><th>Conta</th><th>Status conta</th><th>Criativo</th><th>Gestor</th><th style="text-align:right">Gasto R$</th><th style="text-align:right">ICs</th><th style="text-align:right">Vendas</th><th style="text-align:right">CPA real</th><th>Status</th><th></th></tr></thead><tbody id="tbody"></tbody></table></div><div class="tfoot"><button class="btn" id="addRow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14m-7-7h14"/></svg>Adicionar criativo</button></div></div>'
  el.innerHTML = h

  // Liga/desliga o status da oferta (ativa/inativa).
  document.getElementById('ofStatusBtn').onclick = async () => {
    of.ativa = of.ativa === false // inverte (default ativa)
    render()
    try {
      await ofertasRepo.update(of.id, { ativa: of.ativa })
    } catch (e) {
      toast('Erro ao salvar', true)
    }
  }

  // Excluir oferta (com confirmação). Os criativos caem por cascade no banco.
  document.getElementById('delOfferBtn').onclick = async () => {
    const alvo = state.ofertas[active]
    if (!alvo) return
    const nome = (alvo.codigo && alvo.codigo.trim()) || 'Oferta #' + (active + 1)
    if (!confirm('Excluir a oferta "' + nome + '" e todos os seus criativos? Não tem desfazer.')) return
    const idx = active
    const removida = state.ofertas.splice(idx, 1)[0]
    active = 0
    overview = true
    render()
    try {
      await ofertasRepo.remove(removida.id)
      toast('Oferta excluída')
    } catch (e) {
      state.ofertas.splice(idx, 0, removida) // rollback
      active = idx
      overview = false
      render()
      toast('Erro ao excluir', true)
    }
  }

  // Campos meta: persistem com debounce; recalculam abas e KPIs sem re-render geral.
  el.querySelectorAll('.meta .fld input').forEach(inp => {
    const salvar = debounce(async () => {
      const snapshot = inp.value
      try {
        await ofertasRepo.update(of.id, {
          [OFERTA_COL[inp.dataset.k]]: ofertaValorColuna(inp.dataset.k, inp.value)
        })
        if (inp.value === snapshot) marcarLimpo(inp)
      } catch (e) {
        toast('Erro ao salvar', true)
      }
    })
    inp.oninput = () => {
      const k = inp.dataset.k
      if (k === 'data') inp.value = maskBRDate(inp.value)
      of[k] = inp.dataset.n ? num(inp.value) ?? inp.value : inp.value
      renderTabs()
      refreshOKpis()
      if (k === 'data') {
        const { salvar: podeSalvar, invalida } = dateFieldState(inp.value)
        inp.classList.toggle('cell-invalid', invalida)
        if (!podeSalvar) {
          marcarLimpo(inp)
          return
        }
      }
      marcarSujo(inp)
      salvar()
    }
    inp.onblur = () => salvar.flush()
  })

  renderOBody()
  document.getElementById('addRow').onclick = async () => {
    const novo = criativoVazio(of.id, posicaoFim(of.criativos))
    of.criativos.push(novo)
    renderOBody()
    refreshOKpis()
    try {
      const salvo = await criativosRepo.insert(novo)
      novo.id = salvo.id // captura o id retornado no objeto local
    } catch (e) {
      toast('Erro ao salvar', true)
    }
  }
}

// Atualiza o contador de linhas do detalhe.
function atualizarContador() {
  const of = state.ofertas[active]
  const oc = document.getElementById('ocount')
  if (of && oc) oc.textContent = of.criativos.length + ' linhas'
}

// Corpo da tabela de criativos.
function renderOBody() {
  const of = state.ofertas[active]
  const tb = document.getElementById('tbody')
  tb.innerHTML = ''
  of.criativos.forEach((c, ri) => {
    const tr = document.createElement('tr')

    // Célula de input editável para um campo do criativo (persiste com debounce).
    const mk = (field, cls, isNum) => {
      const td = document.createElement('td')
      const inp = document.createElement('input')
      inp.className = 'cell' + (cls ? ' ' + cls : '')
      inp.value = c[field] ?? ''
      if (field === 'data') {
        inp.inputMode = 'numeric'
        inp.maxLength = 10
        inp.placeholder = 'dd/mm/aaaa'
      }
      const salvar = debounce(async () => {
        const snapshot = inp.value
        try {
          await criativosRepo.update(c.id, {
            [CRIATIVO_COL[field]]: criativoValorColuna(field, inp.value)
          })
          if (inp.value === snapshot) marcarLimpo(inp)
        } catch (e) {
          toast('Erro ao salvar', true)
        }
      })
      inp.oninput = () => {
        if (field === 'data') inp.value = maskBRDate(inp.value)
        c[field] = isNum ? num(inp.value) ?? inp.value : inp.value
        updateORow(ri)
        refreshOKpis()
        if (field === 'data') {
          const { salvar: podeSalvar, invalida } = dateFieldState(inp.value)
          inp.classList.toggle('cell-invalid', invalida)
          if (!podeSalvar) {
            marcarLimpo(inp)
            return
          }
        }
        if (c.id) {
          marcarSujo(inp)
          salvar()
        }
      }
      inp.onblur = () => salvar.flush()
      td.appendChild(inp)
      return td
    }

    tr.appendChild(mk('data'))
    tr.appendChild(mk('conta'))

    // Status conta (ATIVA/INATIVA) — select. Re-render só desta linha + KPIs.
    const tdS = document.createElement('td')
    const sel = document.createElement('select')
    sel.className = 'cell'
    ;['ATIVA', 'INATIVA'].forEach(o => {
      const op = document.createElement('option')
      op.value = o
      op.textContent = o
      if (c.status === o) op.selected = true
      sel.appendChild(op)
    })
    sel.onchange = async () => {
      c.status = sel.value
      updateORow(ri)
      refreshOKpis()
      try {
        if (c.id) await criativosRepo.update(c.id, { status: sel.value })
      } catch (e) {
        toast('Erro ao salvar', true)
      }
    }
    tdS.appendChild(sel)
    tr.appendChild(tdS)

    tr.appendChild(mk('criativo', 'crea'))
    tr.appendChild(mk('gestor'))
    tr.appendChild(mk('gasto', 'num', 1))
    tr.appendChild(mk('ic', 'num', 1))
    tr.appendChild(mk('vendas', 'num', 1))

    const tdC = document.createElement('td')
    tdC.className = 'cpa'
    tdC.id = 'cpa' + ri
    tr.appendChild(tdC)
    const tdSt = document.createElement('td')
    tdSt.id = 'st' + ri
    tr.appendChild(tdSt)

    const tdD = document.createElement('td')
    const d = document.createElement('span')
    d.className = 'del'
    d.innerHTML = svgDel
    d.onclick = async () => {
      const idx = ri
      const removido = of.criativos.splice(idx, 1)[0]
      // Se a oferta ficar sem criativos, recria uma linha em branco (como o legado).
      let recriado = null
      if (!of.criativos.length) {
        recriado = criativoVazio(of.id, posicaoFim(of.criativos))
        of.criativos.push(recriado)
      }
      renderOBody()
      refreshOKpis()
      try {
        if (removido && removido.id) await criativosRepo.remove(removido.id)
        if (recriado) {
          const salvo = await criativosRepo.insert(recriado)
          recriado.id = salvo.id
        }
      } catch (e) {
        // rollback: desfaz a recriação e devolve o removido à posição original
        if (recriado) {
          const j = of.criativos.indexOf(recriado)
          if (j !== -1) of.criativos.splice(j, 1)
        }
        of.criativos.splice(idx, 0, removido)
        renderOBody()
        refreshOKpis()
        toast('Erro ao excluir', true)
      }
    }
    tdD.appendChild(d)
    tr.appendChild(tdD)

    tb.appendChild(tr)
    updateORow(ri)
  })
  atualizarContador()
}

// Atualiza CPA real e badge de status de uma linha específica.
function updateORow(ri) {
  const of = state.ofertas[active],
    c = of.criativos[ri]
  const cc = document.getElementById('cpa' + ri),
    sc = document.getElementById('st' + ri)
  if (!cc) return
  cc.textContent = cpaReal(c)
  const st = statusRow(c, of)
  sc.innerHTML = st ? '<span class="st ' + st + '">' + stLabel[st] + '</span>' : ''
}

// Recalcula KPIs, veredito e contador do detalhe (sem re-renderizar a tabela).
function refreshOKpis() {
  const of = state.ofertas[active],
    s = ostats(of)
  const kp = document.querySelector('#detail .kpis')
  if (kp) kp.outerHTML = kpisDetalheHTML(s)
  const [vt] = verdict(s)
  const vb = document.getElementById('vbox')
  if (vb) {
    vb.className = 'verd ' + vt
    vb.textContent = verdFull(of, s, vt)
  }
  atualizarContador()
  renderTabs()
}

// Botão "Nova oferta".
export function ligarNovaOferta() {
  document.getElementById('newBtn').onclick = async () => {
    const nova = ofertaVazia()
    nova.posicao = posicaoFim(state.ofertas)
    state.ofertas.push(nova)
    active = state.ofertas.length - 1
    overview = false
    try {
      const salva = await ofertasRepo.insert(nova)
      nova.id = salva.id
      // cria 1 criativo em branco já com oferta_id e captura o id
      const c = criativoVazio(nova.id, 0)
      const salvo = await criativosRepo.insert(c)
      c.id = salvo.id
      nova.criativos.push(c)
      render()
      toast('Oferta criada')
    } catch (e) {
      // rollback: a criação falhou — remove a oferta otimista e volta à visão geral
      const i = state.ofertas.indexOf(nova)
      if (i !== -1) state.ofertas.splice(i, 1)
      active = 0
      overview = true
      render()
      toast('Erro ao salvar — oferta não criada', true)
    }
  }
}

// Recebe um evento realtime para a área (re-render preservando navegação).
export function onExternalChange() {
  if (active >= state.ofertas.length) active = Math.max(0, state.ofertas.length - 1)
  render()
}
